import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../../components/context/LoginContext/AuthContext";

/**
 * @typedef {Object} PaymentProof
 * @property {number} id
 * @property {string} name
 * @property {string} type
 * @property {string} url
 * @property {string} uploaded_at
 * @property {number} uploaded_by
 */

/**
 * @typedef {Object} Invoice
 * @property {number} id
 * @property {string} tenant_id
 * @property {number} institution_id
 * @property {number} cycle_id
 * @property {string} invoice_no
 * @property {string} total_amount
 * @property {string} paid_amount
 * @property {string} status
 * @property {string} issued_at
 * @property {string} due_at
 * @property {any} meta
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef {Object} InvoicePayment
 * @property {number} id
 * @property {string} tenant_id
 * @property {number} institution_id
 * @property {number} invoice_id
 * @property {string} amount
 * @property {string} source
 * @property {string} method
 * @property {string} status
 * @property {string|null} gateway_order_id
 * @property {string|null} gateway_payment_id
 * @property {any} gateway_payload
 * @property {string} reference_id
 * @property {any} reference_meta
 * @property {boolean} is_verified
 * @property {number|null} processed_by
 * @property {string|null} processed_at
 * @property {string|null} admin_remarks
 * @property {string|null} invoice_pdf_path
 * @property {string} created_at
 * @property {string} updated_at
 * @property {PaymentProof[]} proofs
 * @property {Invoice} invoice
 */

/**
 * Admin - View Single School Payment
 */
export const useViewSingleInvoicePayment = (paymentId) => {
  const { user, fetchWithAuth } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  /** @type {[InvoicePayment|null, Function]} */
  const [payment, setPayment] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPayment = useCallback(async () => {
    if (!user?.token || !paymentId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(
        `${apiUrl}/school/payments/${paymentId}`,
        { method: "GET" }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch payment details");
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to fetch payment details");
      }

      setPayment(result.data || null);
    } catch (err) {
      console.error("Payment Fetch Error:", err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [apiUrl, fetchWithAuth, user, paymentId]);

  useEffect(() => {
    fetchPayment();
  }, [fetchPayment]);

  return {
    payment,
    loading,
    error,
    refetch: fetchPayment,
  };
};
